/**
 * AskWS Client Library
 * A WebSocket client library for AskWS server
 * Compatible with all modern browsers
 */

(function (global) {
  'use strict';

  // Connection states
  var CONNECTION_STATE = {
    CONNECTING: 'connecting',
    CONNECTED: 'connected',
    DISCONNECTED: 'disconnected',
    FAILED: 'failed'
  };

  var ACTIVITY_TIMEOUT = 25;

  /**
   * Channel class for managing channel subscriptions
   */
  function Channel(name, client) {
    this.name = name;
    this.client = client;
    this.subscribed = false;
    this.pending = false; // Subscription requested but connection not yet established
    this.bindings = {};
  }

  Channel.prototype = {
    /**
     * Subscribe to this channel.
     * If the connection is not yet established, the subscription is queued
     * and will be sent automatically once the connection succeeds.
     * Throws only if the connection has definitively failed.
     */
    subscribe: function () {
      if (this.subscribed) {
        return this;
      }

      if (this.client.state === CONNECTION_STATE.FAILED) {
        throw new Error('Cannot subscribe: connection failed');
      }

      if (this.client.state !== CONNECTION_STATE.CONNECTED) {
        // Queue the subscription until the connection is established
        this.pending = true;
        return this;
      }

      this.pending = false;

      this.client._send({
        event: 'pusher:subscribe',
        data: {
          channel: this.name
        }
      });

      return this;
    },

    /**
     * Unsubscribe from this channel
     */
    unsubscribe: function () {
      // Cancel a queued subscription that was never sent
      if (this.pending && !this.subscribed) {
        this.pending = false;
        this.client._channels.delete(this.name);
        this.bindings = {};
        return this;
      }

      if (!this.subscribed) {
        return this;
      }

      this.client._send({
        event: 'pusher:unsubscribe',
        data: {
          channel: this.name
        }
      });

      this.subscribed = false;
      this.pending = false;
      this.client._channels.delete(this.name);
      this.bindings = {};

      return this;
    },

    /**
     * Bind to an event on this channel
     * @param {string} eventName - Event name to bind to
     * @param {Function} callback - Callback function
     */
    bind: function (eventName, callback) {
      if (typeof callback !== 'function') {
        throw new Error('Callback must be a function');
      }

      if (!this.bindings[eventName]) {
        this.bindings[eventName] = [];
      }

      this.bindings[eventName].push(callback);

      return this;
    },

    /**
     * Unbind from an event on this channel
     * @param {string} eventName - Event name to unbind from
     * @param {Function} callback - Optional callback to remove specific binding
     */
    unbind: function (eventName, callback) {
      if (!this.bindings[eventName]) {
        return this;
      }

      if (callback) {
        var index = this.bindings[eventName].indexOf(callback);
        if (index > -1) {
          this.bindings[eventName].splice(index, 1);
        }
      } else {
        delete this.bindings[eventName];
      }

      return this;
    },

    /**
     * Trigger all callbacks for an event
     * @private
     */
    _trigger: function (eventName, data) {
      if (!this.bindings[eventName]) {
        return;
      }

      var callbacks = this.bindings[eventName].slice();
      for (var i = 0; i < callbacks.length; i++) {
        try {
          callbacks[i](data);
        } catch (error) {
          this.client._emit('error', {
            type: 'callback_error',
            error: error,
            channel: this.name,
            event: eventName
          });
        }
      }
    }
  };

  /**
   * Main AskWS client class
   */
  function AskWS(options) {
    if (!options) {
      throw new Error('Options are required');
    }

    if (!options.host) {
      throw new Error('host is required');
    }

    if (!options.appKey) {
      throw new Error('appKey is required');
    }

    if (!options.token && !options.getToken) {
      throw new Error('Either token or getToken function is required');
    }

    // Configuration
    this.host = options.host;
    this.appKey = options.appKey;
    this.token = options.token; // Initial token (optional if getToken is provided)
    this.getToken = options.getToken; // Function to get fresh token (optional)
    this.encrypted = options.encrypted !== false; // Default to true (wss)
    this.autoReconnect = options.autoReconnect !== false; // Default to true
    this.maxReconnectAttempts = options.maxReconnectAttempts || 10;
    this.reconnectDelay = options.reconnectDelay || 1000; // Initial delay in ms
    this.maxReconnectDelay = options.maxReconnectDelay || 30000; // Max delay in ms
    this.pongTimeout = options.pongTimeout || 10000; // Max wait for an answer to a ping, in ms

    // State
    this.state = CONNECTION_STATE.DISCONNECTED;
    this.socket = null;
    this.socketId = null;
    this.activityTimeout = ACTIVITY_TIMEOUT; // Default 25 seconds
    this.pingInterval = null;
    this._pongTimer = null;

    // Channels
    this._channels = new Map();

    // Event bindings
    this._bindings = {};

    // Reconnection state
    this._wantConnection = false; // connect() called and not disconnect()
    this._reconnectAttempts = 0;
    this._reconnectTimer = null;
    this._reconnecting = false;

    // Back-forward cache handling
    this._bfcacheSuspended = false;
    this._wasActiveBeforeBfcache = false;

    // Connection state change handlers
    this._stateChangeCallbacks = [];

    this._setupBfcacheHandling();
    this._setupLivenessChecks();
  }

  AskWS.prototype = {
    /**
     * Connect to the WebSocket server
     */
    connect: function () {
      this._wantConnection = true;

      if (this.state === CONNECTION_STATE.CONNECTING || this.state === CONNECTION_STATE.CONNECTED) {
        return this;
      }

      this._connect();
      return this;
    },

    /**
     * Internal connection method
     * @private
     */
    _connect: function () {
      var self = this;

      // If getToken is provided, fetch a fresh token before connecting
      if (this.getToken) {
        var tokenPromise = Promise.resolve(this.getToken());
        tokenPromise
          .then(function (token) {
            if (!token || typeof token !== 'string') {
              throw new Error('getToken must return a non-empty string');
            }
            self.token = token;
            self._doConnect();
          })
          .catch(function (error) {
            self._handleError({
              type: 'token_fetch_error',
              error: error
            });

            // During a reconnection cycle (e.g. still offline), keep retrying
            if (self.autoReconnect && self._reconnectAttempts > 0) {
              self._updateState(CONNECTION_STATE.DISCONNECTED);
              self._attemptReconnect();
            } else {
              self._updateState(CONNECTION_STATE.FAILED);
            }
          });
      } else {
        // Use the static token
        this._doConnect();
      }
    },

    /**
     * Perform the actual WebSocket connection
     * @private
     */
    _doConnect: function () {
      this._updateState(CONNECTION_STATE.CONNECTING);

      try {
        // Validate host to prevent SSRF attacks
        // Only allow hostname:port format, no protocol or path
        if (!/^[a-zA-Z0-9.-]+(:\d+)?$/.test(this.host)) {
          throw new Error('Invalid host format. Must be hostname or hostname:port');
        }

        // Validate appKey to prevent path injection
        if (!/^[a-zA-Z0-9._-]+$/.test(this.appKey)) {
          throw new Error('Invalid appKey format');
        }

        if (!this.token) {
          throw new Error('Token is required but not available');
        }

        var protocol = this.encrypted ? 'wss' : 'ws';
        var url = protocol + '://' + this.host + '/app/' + encodeURIComponent(this.appKey);
        // Token is sent directly in the protocol header (no signature needed)
        var protocolHeader = this.token;

        this.socket = new WebSocket(url, [protocolHeader]);

        this.socket.onopen = this._onOpen.bind(this);
        this.socket.onmessage = this._onMessage.bind(this);
        this.socket.onclose = this._onClose.bind(this);
        this.socket.onerror = this._onError.bind(this);
      } catch (error) {
        this._handleError({
          type: 'connection_error',
          error: error
        });
        this._updateState(CONNECTION_STATE.FAILED);
      }
    },

    /**
     * Disconnect from the WebSocket server
     */
    disconnect: function () {
      this._wantConnection = false;
      this.autoReconnect = false;
      this._clearReconnectTimer();
      this._clearPingInterval();

      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }

      this._updateState(CONNECTION_STATE.DISCONNECTED);
      return this;
    },

    /**
     * Force a fresh connection (e.g. after back-forward cache restore)
     */
    forceReconnect: function () {
      this._bfcacheSuspended = false;
      this._clearReconnectTimer();
      this._clearPingInterval();
      this._closeSocketSilently();

      this.socketId = null;
      this._reconnecting = false;
      // Counts as the first step of a reconnection cycle, so a failure
      // keeps retrying with backoff instead of staying disconnected
      this._reconnectAttempts = 1;
      this._updateState(CONNECTION_STATE.DISCONNECTED);
      this._connect();
      return this;
    },

    /**
     * Subscribe to a channel.
     * If not connected yet, the subscription is queued and flushed
     * automatically on pusher:connection_established.
     * @param {string} channelName - Name of the channel
     */
    subscribe: function (channelName) {
      if (!channelName || typeof channelName !== 'string') {
        throw new Error('Channel name must be a non-empty string');
      }

      var channel = this._channels.get(channelName);
      if (!channel) {
        channel = new Channel(channelName, this);
        this._channels.set(channelName, channel);
      }

      channel.subscribe();
      return channel;
    },

    /**
     * Unsubscribe from a channel
     * @param {string} channelName - Name of the channel
     */
    unsubscribe: function (channelName) {
      var channel = this._channels.get(channelName);
      if (channel) {
        channel.unsubscribe();
      }
      return this;
    },

    /**
     * Get a channel by name
     * @param {string} channelName - Name of the channel
     */
    channel: function (channelName) {
      return this._channels.get(channelName) || null;
    },

    /**
     * Get all subscribed channels
     */
    allChannels: function () {
      var channels = [];
      this._channels.forEach(function (channel) {
        if (channel.subscribed) {
          channels.push(channel);
        }
      });
      return channels;
    },

    /**
     * Bind to a global event
     * @param {string} eventName - Event name
     * @param {Function} callback - Callback function
     */
    bind: function (eventName, callback) {
      if (typeof callback !== 'function') {
        throw new Error('Callback must be a function');
      }

      if (!this._bindings[eventName]) {
        this._bindings[eventName] = [];
      }

      this._bindings[eventName].push(callback);
      return this;
    },

    /**
     * Unbind from a global event
     * @param {string} eventName - Event name
     * @param {Function} callback - Optional callback to remove specific binding
     */
    unbind: function (eventName, callback) {
      if (!this._bindings[eventName]) {
        return this;
      }

      if (callback) {
        var index = this._bindings[eventName].indexOf(callback);
        if (index > -1) {
          this._bindings[eventName].splice(index, 1);
        }
      } else {
        delete this._bindings[eventName];
      }

      return this;
    },

    /**
     * Bind to connection state changes
     * @param {Function} callback - Callback function(state, previousState)
     */
    bind_global: function (callback) {
      if (typeof callback !== 'function') {
        throw new Error('Callback must be a function');
      }

      this._stateChangeCallbacks.push(callback);
      return this;
    },

    /**
     * Unbind from connection state changes
     * @param {Function} callback - Callback function to remove
     */
    unbind_global: function (callback) {
      var index = this._stateChangeCallbacks.indexOf(callback);
      if (index > -1) {
        this._stateChangeCallbacks.splice(index, 1);
      }
      return this;
    },

    /**
     * Send a message to the server
     * @private
     */
    _send: function (message) {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        throw new Error('Cannot send message: not connected');
      }

      try {
        this.socket.send(JSON.stringify(message));
      } catch (error) {
        this._handleError({
          type: 'send_error',
          error: error
        });
        throw error;
      }
    },

    /**
     * Handle WebSocket open event
     * @private
     */
    _onOpen: function () {
      // Connection established, waiting for pusher:connection_established
    },

    /**
     * Handle WebSocket message event
     * @private
     */
    _onMessage: function (event) {
      // Any message from the server proves the connection is alive
      this._clearPongTimer();

      try {
        var message = JSON.parse(event.data);

        // Handle protocol messages
        if (message.event === 'pusher:connection_established') {
          this._handleConnectionEstablished(message.data);
        } else if (message.event === 'pusher:subscription_succeeded') {
          this._handleSubscriptionSucceeded(message.channel);
        } else if (message.event === 'pusher:pong') {
          this._emit('pusher:pong', {});
        } else {
          // Handle channel events or generic broadcasts
          this._handleChannelMessage(message);
        }
      } catch (error) {
        this._handleError({
          type: 'message_parse_error',
          error: error,
          rawData: event.data
        });
      }
    },

    /**
     * Handle connection established message
     * @private
     */
    _handleConnectionEstablished: function (data) {
      this.socketId = data.socket_id;
      this.activityTimeout = data.activity_timeout || ACTIVITY_TIMEOUT;

      this._updateState(CONNECTION_STATE.CONNECTED);
      this._startPingInterval();
      this._reconnectAttempts = 0;
      this._reconnecting = false;

      // Resubscribe to all channels and flush pending subscriptions
      this._resubscribeAll();

      this._emit('connection_established', {
        socket_id: this.socketId,
        activity_timeout: this.activityTimeout
      });
    },

    /**
     * Handle subscription succeeded message
     * @private
     */
    _handleSubscriptionSucceeded: function (channelName) {
      var channel = this._channels.get(channelName);
      if (channel) {
        channel.subscribed = true;
        channel.pending = false;
        channel._trigger('pusher:subscription_succeeded', {});
        this._emit('subscription_succeeded', {
          channel: channelName
        });
      }
    },

    /**
     * Handle channel messages (broadcasts or custom events)
     * @private
     */
    _handleChannelMessage: function (message) {
      // Check if message has event and channel properties (structured format)
      if (message.event && message.channel) {
        var channel = this._channels.get(message.channel);
        if (channel && channel.subscribed) {
          channel._trigger(message.event, message.data || message);
        }
        this._emit('message', message);
      } else if (message.channel) {
        // Message has channel but no event - treat as generic broadcast
        var channel = this._channels.get(message.channel);
        if (channel && channel.subscribed) {
          channel._trigger('message', message);
        }
        this._emit('message', message);
      } else {
        // Generic message without channel - emit globally
        this._emit('message', message);
      }
    },

    /**
     * Resubscribe to all channels after (re)connection,
     * including subscriptions queued while disconnected
     * @private
     */
    _resubscribeAll: function () {
      var channelsToSubscribe = [];
      this._channels.forEach(function (channel) {
        if (channel.subscribed || channel.pending) {
          channelsToSubscribe.push(channel);
        }
      });

      // Reset subscribed state and (re)subscribe
      for (var i = 0; i < channelsToSubscribe.length; i++) {
        channelsToSubscribe[i].subscribed = false;
        channelsToSubscribe[i].subscribe();
      }
    },

    /**
     * Notify pending subscriptions that the connection definitively failed
     * @private
     */
    _failPendingSubscriptions: function () {
      var self = this;
      this._channels.forEach(function (channel) {
        if (channel.pending && !channel.subscribed) {
          channel.pending = false;
          channel._trigger('pusher:subscription_error', {
            reason: 'connection_failed'
          });
          self._emit('subscription_error', {
            channel: channel.name,
            reason: 'connection_failed'
          });
        }
      });
    },

    /**
     * Handle WebSocket close event
     * @private
     */
    _onClose: function (event) {
      this._clearPingInterval();
      this.socket = null;
      this.socketId = null;

      // Update state
      var wasConnected = this.state === CONNECTION_STATE.CONNECTED;
      this._updateState(CONNECTION_STATE.DISCONNECTED);

      // Emit close event
      this._emit('disconnected', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean
      });

      // Attempt reconnection if enabled, also when a reconnection attempt
      // itself failed (otherwise the client gives up after a single retry)
      var retrying = this._reconnectAttempts > 0;
      if (this.autoReconnect && (wasConnected || retrying) && !this._bfcacheSuspended) {
        this._attemptReconnect();
      }
    },

    /**
     * Handle WebSocket error event
     * @private
     */
    _onError: function (error) {
      this._handleError({
        type: 'websocket_error',
        error: error
      });
    },

    /**
     * Attempt to reconnect
     * @private
     */
    _attemptReconnect: function () {
      if (this._reconnecting) {
        return;
      }

      if (this._reconnectAttempts >= this.maxReconnectAttempts) {
        this._updateState(CONNECTION_STATE.FAILED);
        this._emit('reconnect_failed', {
          attempts: this._reconnectAttempts
        });
        return;
      }

      this._reconnecting = true;
      this._reconnectAttempts++;

      var backoff = Math.min(
        this.reconnectDelay * Math.pow(2, this._reconnectAttempts - 1),
        this.maxReconnectDelay
      );

      // Random half of the backoff: when the server drops every socket at
      // once (deploy, restart), clients don't all come back in the same ms
      var delay = Math.round(backoff / 2 + Math.random() * backoff / 2);

      this._emit('reconnecting', {
        attempt: this._reconnectAttempts,
        delay: delay
      });

      var self = this;
      this._reconnectTimer = setTimeout(function () {
        self._reconnecting = false;
        self._connect();
      }, delay);
    },

    /**
     * Clear reconnect timer
     * @private
     */
    _clearReconnectTimer: function () {
      if (this._reconnectTimer) {
        clearTimeout(this._reconnectTimer);
        this._reconnectTimer = null;
      }
    },

    /**
     * Start ping interval
     * @private
     */
    _startPingInterval: function () {
      this._clearPingInterval();

      var self = this;
      var interval = this.activityTimeout * 1000; // Convert to milliseconds

      this.pingInterval = setInterval(function () {
        self._sendPing();
      }, interval);
    },

    /**
     * Send a ping and expect any message back within pongTimeout.
     * Without an answer the socket is considered dead (e.g. half-open
     * after a network change or a mobile app suspension) and replaced.
     * @private
     */
    _sendPing: function () {
      if (this.state !== CONNECTION_STATE.CONNECTED) {
        return;
      }

      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        return;
      }

      try {
        this._send({ event: 'pusher:ping' });
      } catch (error) {
        this._handleError({
          type: 'ping_error',
          error: error
        });
        return;
      }

      // Keep the earliest deadline if a ping is already waiting for its pong
      if (this._pongTimer) {
        return;
      }

      var self = this;
      this._pongTimer = setTimeout(function () {
        self._pongTimer = null;
        self._handlePongTimeout();
      }, this.pongTimeout);
    },

    /**
     * Drop a socket that stopped answering and go through the regular
     * disconnect path, so listeners see it and reconnection kicks in
     * @private
     */
    _handlePongTimeout: function () {
      if (this.state !== CONNECTION_STATE.CONNECTED) {
        return;
      }

      // socket.close() would wait for a closing handshake that never comes
      this._closeSocketSilently();
      this._onClose({
        code: 4000,
        reason: 'pong_timeout',
        wasClean: false
      });
    },

    /**
     * Clear pending pong timeout
     * @private
     */
    _clearPongTimer: function () {
      if (this._pongTimer) {
        clearTimeout(this._pongTimer);
        this._pongTimer = null;
      }
    },

    /**
     * Clear ping interval
     * @private
     */
    _clearPingInterval: function () {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }

      this._clearPongTimer();
    },

    /**
     * Update connection state
     * @private
     */
    _updateState: function (newState) {
      if (this.state === newState) {
        return;
      }

      var previousState = this.state;
      this.state = newState;

      // Connection definitively failed: reject queued subscriptions
      if (newState === CONNECTION_STATE.FAILED) {
        this._failPendingSubscriptions();
      }

      // Trigger state change callbacks
      for (var i = 0; i < this._stateChangeCallbacks.length; i++) {
        try {
          this._stateChangeCallbacks[i](newState, previousState);
        } catch (error) {
          // Silently handle errors in state change callbacks
        }
      }

      this._emit('state_change', {
        current: newState,
        previous: previousState
      });
    },

    /**
     * Emit an event
     * @private
     */
    _emit: function (eventName, data) {
      if (!this._bindings[eventName]) {
        return;
      }

      var callbacks = this._bindings[eventName].slice();
      for (var i = 0; i < callbacks.length; i++) {
        try {
          callbacks[i](data);
        } catch (error) {
          // Handle callback errors silently to prevent breaking other callbacks
        }
      }
    },

    /**
     * Close the socket without triggering reconnect side effects
     * @private
     */
    _closeSocketSilently: function () {
      if (!this.socket) {
        return;
      }

      var socket = this.socket;
      this.socket = null;
      socket.onclose = null;
      socket.onerror = null;
      socket.onmessage = null;
      socket.onopen = null;

      try {
        socket.close();
      } catch (error) {
        // Ignore close errors on stale sockets
      }
    },

    /**
     * Suspend the connection before the page enters the back-forward cache
     * @private
     */
    _suspendForBfcache: function () {
      this._bfcacheSuspended = true;
      this._wasActiveBeforeBfcache = true;
      this._clearReconnectTimer();
      this._clearPingInterval();
      this._closeSocketSilently();
      this.socketId = null;

      if (this.state === CONNECTION_STATE.CONNECTED || this.state === CONNECTION_STATE.CONNECTING) {
        this._emit('bfcache_suspend', {
          previous_state: this.state
        });
        this._updateState(CONNECTION_STATE.DISCONNECTED);
      }
    },

    /**
     * Reconnect after a back-forward cache restore when needed
     * @private
     */
    _resumeFromBfcache: function () {
      if (this._wasActiveBeforeBfcache) {
        this._wasActiveBeforeBfcache = false;
        this._bfcacheSuspended = false;
        this._emit('bfcache_resume', { reason: 'suspended' });
        this.forceReconnect();
        return;
      }

      if (this.state !== CONNECTION_STATE.CONNECTED && this.state !== CONNECTION_STATE.CONNECTING) {
        return;
      }

      var socketDead = !this.socket || this.socket.readyState !== WebSocket.OPEN;
      if (socketDead) {
        this._emit('bfcache_resume', {
          reason: 'stale_socket',
          socket_ready_state: this.socket ? this.socket.readyState : null
        });
        this.forceReconnect();
      }
    },

    /**
     * Listen for back-forward cache navigation events
     * @private
     */
    _setupBfcacheHandling: function () {
      if (typeof window === 'undefined' || !window.addEventListener) {
        return;
      }

      var self = this;

      window.addEventListener('pagehide', function (event) {
        if (!event.persisted) {
          return;
        }

        if (self.state === CONNECTION_STATE.CONNECTED || self.state === CONNECTION_STATE.CONNECTING) {
          self._suspendForBfcache();
        }
      });

      window.addEventListener('pageshow', function (event) {
        if (!event.persisted) {
          return;
        }

        self._resumeFromBfcache();
      });
    },

    /**
     * Check the connection right away when the page comes back to the
     * foreground or the network comes back, instead of waiting for the
     * next ping interval: timers and sockets may have been frozen meanwhile
     * @private
     */
    _setupLivenessChecks: function () {
      if (typeof window === 'undefined' || !window.addEventListener) {
        return;
      }

      var self = this;

      if (typeof document !== 'undefined' && document.addEventListener) {
        document.addEventListener('visibilitychange', function () {
          if (document.visibilityState === 'visible') {
            self._checkConnection('visible');
          }
        });
      }

      window.addEventListener('online', function () {
        self._checkConnection('online');
      });
    },

    /**
     * Connected: verify the socket with a ping. Disconnected (waiting for a
     * backoff retry, or given up): reconnect now instead of waiting
     * @private
     */
    _checkConnection: function (reason) {
      if (!this._wantConnection || !this.autoReconnect || this._bfcacheSuspended) {
        return;
      }

      if (this.state === CONNECTION_STATE.CONNECTED) {
        this._sendPing();
        return;
      }

      if (this.state === CONNECTION_STATE.CONNECTING) {
        return;
      }

      // Restart the backoff from its first step: a failure retries quickly
      this._clearReconnectTimer();
      this._reconnecting = false;
      this._reconnectAttempts = 1;

      this._emit('reconnecting', {
        attempt: this._reconnectAttempts,
        delay: 0,
        reason: reason
      });

      this._connect();
    },

    /**
     * Handle errors
     * @private
     */
    _handleError: function (errorInfo) {
      this._emit('error', errorInfo);

      // Also throw if no error handlers are registered
      if (!this._bindings['error'] || this._bindings['error'].length === 0) {
        var error = new Error(errorInfo.type || 'Unknown error');
        error.details = errorInfo;
        throw error;
      }
    }
  };

  // Export for different environments
  if (typeof module !== 'undefined' && module.exports) {
    // Node.js/CommonJS
    module.exports = AskWS;
  } else if (typeof define === 'function' && define.amd) {
    // AMD
    define(function () {
      return AskWS;
    });
  } else {
    // Browser global
    global.AskWS = AskWS;
  }

})(typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : this);